import axiosInstance from '../utils/axiosInstance';

export async function getDashboardStats(companyId){
    const { data } = await axiosInstance.get('/admin-dashboard/stats', { params: { companyId }, workspace: "admin" });
    return Array.isArray(data) ? data : data?.data ?? [];
}

// Salary Components APIs
export async function getComponentTypes(category) {
    const { data } = await axiosInstance.get(`/admin/salary-components/types/${category}`);
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function createSalaryComponent(payload) {
    const { data } = await axiosInstance.post('/admin/salary-components',payload);
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function getSalaryComponents(category) {
    const { data } = await axiosInstance.get(`/admin/salary-components`, {params: { category } });
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function updateSalaryComponent(payload, componentId) {
    const { data } = await axiosInstance.put(`/admin/salary-components/${componentId}`, payload);
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function deleteSalaryComponent(componentId) {
    const { data } = await axiosInstance.delete(`/admin/salary-components/${componentId}`);
    return Array.isArray(data) ? data : data?.data ?? [];
}

// Employee APIs
export async function getEmployees(skip, limit, options = {}) {
  const params = {
    skip,
    limit,
    ...(options.statuses?.length ? { statuses: options.statuses.join(",") }
        : {}),
    ...(options.scope ? {scope:options.scope} : {}),    
  };

    const {data} = await axiosInstance.get('/admin/employees', { params });
    return Array.isArray(data) ? data : data ?? [];
}

// Bulk / department-wide manager assignment.
// payload: { manager_id, employee_ids? | department?, replace_existing?, dry_run? }
// dry_run returns counts; a real run needs an explicit replace_existing.
export async function assignManager(payload) {
  const { data } = await axiosInstance.post("/admin/employees/assign-manager", payload);
  return data?.data ?? data;
}

// Departments with their ongoing manager rule and headcount ([] without the feature).
// Everyone who could manage employees — admins and employees with a login.
// Each: { _id, name, email, is_admin, employee_code, designation, department, can_manage }
export async function getManagerCandidates() {
  const { data } = await axiosInstance.get("/admin/manager-candidates");
  return data?.data ?? [];
}

// What managers do for their direct reports: { leave, appraisals, pips, goals }
export async function getManagerDuties() {
  const { data } = await axiosInstance.get("/admin/manager-duties");
  return data?.data ?? data;
}

export async function updateManagerDuties(duties) {
  const { data } = await axiosInstance.put("/admin/manager-duties", duties);
  return data?.data ?? data;
}

export async function getDepartmentManagers() {
  const { data } = await axiosInstance.get("/admin/department-managers");
  return Array.isArray(data) ? data : data?.data ?? [];
}

// Stop a department's ongoing rule; employees keep their current manager.
export async function clearDepartmentManager(departmentId) {
  const { data } = await axiosInstance.delete(`/admin/department-managers/${departmentId}`);
  return data;
}

export async function deleteEmployees(employeeId) {
  const { data } = await axiosInstance.delete(`/admin/employees/${employeeId}`);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function getEarningSalaryComponents(category) {
  const { data } = await axiosInstance.get(`/admin/earning-salary-components`, { params: { category } });
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function createEmployee(data) {
  const response = await axiosInstance.post('/admin/employees', data);
  return response
}

export async function validateImportEmployees(employees) {
  const data = await axiosInstance.post('/admin/validate-import', employees);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function finalizeImportEmployees(employees, isOverWriteAllowed) {
  const { data } = await axiosInstance.post('/admin/finalize-import', { employees: employees, overwrite: isOverWriteAllowed });
  return data
}

export async function getEmployeeById(employeeId) {
  const response = await axiosInstance.get(`/admin/employees/id/${employeeId}`);
  return response
}

// Off-platform companies request more seats from the super-admin instead of
// buying online. The client only says how many MORE seats it wants.
export async function requestMoreSeats(extraEmployees, note) {
  const { data } = await axiosInstance.post('/admin/seat-requests', {
    extra_employees: extraEmployees,
    note: note || undefined,
  });
  return data;
}

export async function getMySeatRequest() {
  const { data } = await axiosInstance.get('/admin/seat-requests/mine');
  return data;
}

// Off-platform companies apply for a PLAN UPGRADE via the super-admin instead
// of paying online. Billing stays offline; the super-admin applies the change.
export async function requestPlanUpgrade(targetPlanId, extraSeats, note) {
  const { data } = await axiosInstance.post('/admin/plan-upgrade-requests', {
    target_plan_id: targetPlanId,
    extra_seats: extraSeats || 0,
    note: note || undefined,
  });
  return data;
}

export async function fetchEmployeeDocumentsByAdmin(id) {
    const res  = await axiosInstance.get(`/employee/documents/${id.employeeId }`);
    return res.data.data ?? [];
}

export async function updateEmployee(data, employeeId) {
  const response = await axiosInstance.put(
    `/admin/employees`,
    data,
    {
      params: {
        employee_id: employeeId,
      },
    }
  );

  return response;
}

export async function getEmployeeLimitStatus(additionalEmployees = 1) {
  const response = await axiosInstance.get(`/admin/employees/limit-status`, {params: {additionalEmployees}});
  return response.data
}

export async function getWorkingLocations() {
  const response = await axiosInstance.get(`/admin/locations`);
  return response.data
}

// Tax Configuration APIs
export async function getTaxConfigurations(configType) {
  const { data } = await axiosInstance.get(`/admin/company-tax-config/${configType}`);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function createTaxConfiguration(payload, configType) {
  const { data } = await axiosInstance.post(`/admin/company-tax-config/${configType}`, payload);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function disableTaxConfiguration(configType) {
  const { data } = await axiosInstance.patch(`/admin/company-tax-config/disable/${configType}`);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function saveProfessionalTaxConfig(payload) {
  const { data } = await axiosInstance.post(`/admin/save-professional-tax`, payload);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function updateLWFStatus(payload) {
  const { data } = await axiosInstance.patch(`/admin/lwf/update`, payload);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function saveTdsConfig(payload) {
  const { data } = await axiosInstance.post(`/admin/tds`, payload);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function getTdsConfig() {
  const { data } = await axiosInstance.get(`/admin/tds`);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function disableTdsConfig(payload) {
  const { data } = await axiosInstance.patch(`/admin/tds/disable`, payload);
  return Array.isArray(data) ? data : data?.data ?? [];
}




// Settings APIs
export async function createSettings(formData) {
  const { data } = await axiosInstance.post("/admin/settings", formData);
  return data;
}

export const uploadCompanyLogo = async (formData) => {
  try {
    const {data} = await axiosInstance.post('/admin/settings/upload-logo', formData);
    return data;
  } catch (error) {
    throw error;
  }
};

export async function getSettings() {
    const {data} = await axiosInstance.get('/admin/settings');
    return data;
}

// ------------------ WORKING DAYS ------------------
export async function saveWorkingDays(working_days_config) {
  const { data } = await axiosInstance.put(
    "/admin/settings/working-days",
    { working_days_config }
  );
  return data;
}

// ------------------ HOLIDAYS CRUD ------------------
export async function fetchHolidays() {
  const { data } = await axiosInstance.get("/admin/settings");
  return data.data.holidays || [];
}

// ADD holiday (no ID)
export async function addHoliday(payload) {
  const { data } = await axiosInstance.post("/admin/holidays", payload);
  return data;
}

// EDIT holiday (with ID)
export async function updateHoliday(id, payload) {
  const { data } = await axiosInstance.put(`/admin/holidays/${id}`, payload);
  return data;
}

export async function deleteHoliday(id) {
  const { data } = await axiosInstance.delete(`/admin/holidays/${id}`);
  return data;
}

// ------------------ IMPORT / EXPORT ------------------
export async function downloadHolidayTemplate() {
  return axiosInstance.get("/admin/holidays/export-template", {
    responseType: "blob",
  });
}

export async function importHolidays(file) {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axiosInstance.post("/admin/holidays/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return data;
}

export async function changePasswordService(payload) {
  const { data } = await axiosInstance.put("/admin/change-password", payload);
  return data;
}

//Bank Account Services
export const saveBankAccountsService = async (bank_accounts) => {
  const res = await axiosInstance.put("/admin/settings/bank-accounts", { bank_accounts });
  return res.data;
};

export const getBankAccounts = async () => {
  const res = await axiosInstance.get("/admin/settings/bank-accounts");
  return res.data || [];
};

export const createBankAccountService = async (data) => {
  const res = await axiosInstance.post("/admin/settings/bank-accounts", data);
  return res.data || [];
}

export const bulkImportBankAccountsService = async (data) => {
  const res = await axiosInstance.post("/admin/settings/bank-accounts/import", data);
  return res.data || [];
}

export const updateBankAccountService = async (id, data) => {
  const res = await axiosInstance.put("/admin/settings/bank-accounts", data, { params: { id }});
  return res.data || [];
}

export const deleteBankAccountService = async (id) => {
  const res = await axiosInstance.delete("/admin/settings/bank-accounts", { params: { id }});
  return res.data || [];
}

export const getCompanyMiscellaneousData = async () => {
  const res = await axiosInstance.get("/admin/company-settings",);
  return res.data;
};

//Locations CRUD
export const createLocationService = async (data) => {
  const res = await axiosInstance.post("/admin/settings/locations", data);
  return res.data;
}

export const updateLocationService = async (id, data) => {
  const res = await axiosInstance.put("/admin/settings/locations", data, { params: { id }});
  return res.data;
}

export const deleteLocationService = async (id) => {
  const res = await axiosInstance.delete("/admin/settings/locations", { params: { id }});
  return res.data;
}

//Departments CRUD
export const createDepartmentService = async (data) => {
  const res = await axiosInstance.post("/admin/settings/departments", data);
  return res.data;
}

export const updateDepartmentService = async (id, data) => {
  const res = await axiosInstance.put("/admin/settings/departments", data, { params: { id }});
  return res.data;
}

export const deleteDepartmentService = async (id) => {
  const res = await axiosInstance.delete("/admin/settings/departments", { params: { id }});
  return res.data;
}

//Designations CRUD
export const createDesignationService = async (data) => {
  const res = await axiosInstance.post("/admin/settings/designations", data);
  return res.data;
}

export const updateDesignationService = async (id, data) => {
  const res = await axiosInstance.put("/admin/settings/designations", data, { params: { id }});
  return res.data;
}

export const deleteDesignationService = async (id) => {
  const res = await axiosInstance.delete("/admin/settings/designations", { params: { id }});
  return res.data;
}

//Event Services
export async function fetchEvents() {
    const { data } = await axiosInstance.get(`/admin/events`);
    return Array.isArray(data) ? data : data ?? [];
}

export async function createEvent( payload) {
    const { data } = await axiosInstance.post(`/admin/events`, payload);
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function updateEvent(eventId, payload) {
    const { data } = await axiosInstance.put(`/admin/events/${eventId}`, payload);
    return Array.isArray(data) ? data : data ?? [];
}

export async function getEventById(eventId) {
    const { data } = await axiosInstance.get(`/admin/events/${eventId}`);
    return Array.isArray(data) ? data : data ?? [];
}

export async function deleteEvent(eventId) {
    const { data } = await axiosInstance.delete(`/admin/events/${eventId}`);
    return Array.isArray(data) ? data : data ?? [];
}

export async function fetchEmployees() {
    const data  = await axiosInstance.get(`/admin/employees`);
    return data.data;
}

//Bank Advice Services
export async function saveAdviceRecord(payload) {
    const { data } = await axiosInstance.post(`/admin/bank-advice`, payload);
    return Array.isArray(data) ? data : data ?? [];
}

export async function getAllBankAdviceHistory() {
    const { data } = await axiosInstance.get(`/admin/getAllBankAdviceHistory`);
    return Array.isArray(data) ? data : data ?? [];
}

export async function chngeAdviceHistoryStatus(id) {
    const { data } = await axiosInstance.post(`/admin/chngeAdviceHistoryStatus/${id}`);
    return Array.isArray(data) ? data : data ?? [];
}

export async function getAdviceHistoryFile(adviceId) {
    const {data} = await axiosInstance.get(`/admin/getAdviceHistoryFile/${adviceId}`);
    return data.data.data;
}

//Leave Config Services
export async function updateCompanyLeaveConfiguration(payload) {
    const {data} = await axiosInstance.put(`/admin/leave-config`, payload);
    return data;
}

export async function fetchCompanyLeaveConfiguration() {
    const {data} = await axiosInstance.get(`/admin/leave-config`);
    return data;
}

export async function deleteLeaveConfiguration(id) {
    const {data} = await axiosInstance.delete(`/admin/leave-config/${id}`);
    return data;
}

export async function updateLeaveConfig(id,payload) {
    const {data} = await axiosInstance.patch(`/admin/leave-config/${id}`,payload);
    return data;
}

//Login details Services
export async function fetchAllLoginDetails() {
  const { data } = await axiosInstance.get(`/admin/login-details`);
  return data;
}

export async function fetchEmployeeLoginHistory(employeeId, days = 365) {
  const { data } = await axiosInstance.get(
    `/admin/employees/${employeeId}/login-history?days=${days}`
  );
  return data;
}export async function fetchAllLoanHistory() {
    const {data} = await axiosInstance.get(`/admin/loan-history`);
    return data;
}

export async function changeLoanRequestStatus(loanId, status) {
    const {data} = await axiosInstance.put(`/admin/loan-request/${loanId}`, status);
    return data;
}

export async function deleteLoanRequest(loanId) {
    const {data} = await axiosInstance.delete(`/admin/loan-request/${loanId}`);
    return data;
}

export async function applyLoan(payload) {
    const {data} = await axiosInstance.post(`/admin/loan-request`,payload);
    return data;
}

export async function addHolidays(payload) {
    const {data} = await axiosInstance.post(`/admin/holidays`,payload);
    return data;
}

export async function editHoliday(id, payload) {
    const {data} = await axiosInstance.put(`/admin/holidays/${id}`,payload);
    return data;
}
export async function addHolidayBulk(file) {
  const formData = new FormData();
  formData.append("file", file)
    const {data} = await axiosInstance.post(`/admin/holidays/import`,formData);
    return data;
}

export async function copyHolidaysFromPreviousYear(importYear, isCopy) {
try {
    const {data} = await axiosInstance.post(`/admin/copy-holidays`,{ importYear, isCopy },
      isCopy? {}: { responseType: "arraybuffer" });
    return data;
} catch (error) {
  console.error(error)
}
}


export async function addLoanType(payload) {
    const {data} = await axiosInstance.post(`/admin/loan-type`,payload);
    return data;
}

export async function updateLoanType(loanId,payload) {
    const {data} = await axiosInstance.put(`/admin/loan-type/${loanId}`,payload);
    return data;
}

export async function deleteLoanType(loanId) {
    const {data} = await axiosInstance.delete(`/admin/loan-type/${loanId}`);
    return data;
}

export async function addReimbursementType(payload) {
    const {data} = await axiosInstance.post(`/admin/reimbursement-type`, payload);
    return data;
}

export async function updateReimbursementType(typeId, payload) {
    const {data} = await axiosInstance.put(`/admin/reimbursement-type/${typeId}`, payload);
    return data;
}

export async function deleteReimbursementType(typeId) {
    const {data} = await axiosInstance.delete(`/admin/reimbursement-type/${typeId}`);
    return data;
}

export async function updateReimbursementSettings(payload) {
    const {data} = await axiosInstance.put(`/admin/reimbursement-settings`, payload);
    return data;
}

export async function uploadEmloyeeDocsByAdmin(data, id) {
    const res  = await axiosInstance.post(`/employee/uploadDocs/${id.employeeId}`, data);
    return res;
}

export async function getDashboardPendingActions() {
    const {data} = await axiosInstance.get(`/admin-dashboard/pendingActions`);
    return data;
}

export async function leaveStatistics() {
    const {data} = await axiosInstance.get(`/admin-dashboard/leaveStatistics`);
    return data;
}

export async function loanOverviewDashboard() {
    const {data} = await axiosInstance.get(`/admin-dashboard/loanOverview`);
    return data;
}

export async function fetchDistribution() {
    const {data} = await axiosInstance.get(`/admin-dashboard/distribution`);
    return data;
}

export async function getPayrollTrends() {
    const {data} = await axiosInstance.get(`/admin-dashboard/payrollTrends`);
    return data;
}

export async function getPayrollAnalyticsStats() {
  const { data } = await axiosInstance.get('/admin-dashboard/payroll-analytics/stats');
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function getMonthlyPayrollTrend() {
  const { data } = await axiosInstance.get('/admin-dashboard/payroll-analytics/monthly-trend');
  return Array.isArray(data) ? data : data?.data ?? [];
}

export const getDepartmentWisePayroll = async () => {
  const data = await axiosInstance.get("/admin-dashboard/payroll-analytics/department-distribution");
  return Array.isArray(data) ? data : data?.data ?? [];
};

export const getPayrollDetailedAnalytics = async ({ page = 1, limit = 6 } = {}) => {
  const { data } = await axiosInstance.get(
    "/admin-dashboard/payroll-analytics/detailed",
    {
      params: { page, limit },
    }
  );
  return Array.isArray(data) ? data : data?.data ?? [];
};

export async function configureRating(payload) {
  const { data } = await axiosInstance.post(`/admin/ratings`, payload);
  return data;
}

export async function fetchCompanyRatingConfiguration() {
  const { data } = await axiosInstance.get(`/admin/ratings`);
  return data.data;
}

export async function updateRatingConfiguration(payload) {
  const { data } = await axiosInstance.put(`/admin/ratings`, payload);
  return data.data;
}

export async function getActivityLogs(params = {}) {
    const { data } = await axiosInstance.get("/admin/activity-logs", {
      params,
    });
    return data?.data ?? data;
}

export async function confirmLoanPrepayment(payload) {
  const { data } = await axiosInstance.post(`/admin/loans/prepayment/confirm`,payload);
    return data;
}

export async function getAllActivityLogs(params = {}, page, limit, month, year) {
  const { data } = await axiosInstance.get("/admin/activity-logs", {
    params: {page: page,limit: limit, month: month, year: year, ...params},
  });
  return data;
}

export const updateAttendanceTrackingSystem = async (paylaod) => {
  const { data } = await axiosInstance.post("/admin/attendance/system-update", paylaod);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function getTodayAttendanceSummary() {
  const { data } = await axiosInstance.get("/admin-dashboard/attendance/today-summary");

  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function toggleEmployeePortal(payload) {
  const { data } = await axiosInstance.post("/admin/employee-portal/toggle", payload);
  return data;
}

export const resetRating = async () => {
  const res = await axiosInstance.put("/admin/resetRating");
  return res;
}

export async function sendEmployeeInvite(employee_id) {
  const { data } = await axiosInstance.post(`/admin/employees/${employee_id}/send-invite`);
  return data;
}

// Admin access for an employee's own login (one login per person): the same
// login then also opens the admin sections the role allows.
export async function grantEmployeeAdminAccess(employeeId, roleId) {
  const { data } = await axiosInstance.post(`/admin/employees/${employeeId}/admin-access`, { roleId });
  return data?.data ?? data;
}

// Someone set up twice — this admin login and their own employee login — becomes
// one login. sign_in_with: "admin" | "employee" (which email + password to keep).
export async function mergeEmployeeLogin(userId, { employee_id, sign_in_with }) {
  const { data } = await axiosInstance.post(`/admin/users/${userId}/merge-employee-login`, {
    employee_id,
    sign_in_with,
  });
  return data?.data ?? data;
}

export async function getCareerHistory(employeeId, options = {}) {
  const { includeOverridden = false } = options;
  const targetEmployeeId = employeeId || "me";
  const { data } = await axiosInstance.get(`/admin/career/${targetEmployeeId}`, {
    params: {
      include_overridden: includeOverridden,
    },
  });
  return data;
}

export async function revertEmployeeTermination(employeeId) {
  const { data } = await axiosInstance.post(`/admin/employees/${employeeId}/revert-termination`);
  return data;
};

export async function sendOtptoAdmin() {
  const { data } = await axiosInstance.post(`/admin/sendOtp`);
  return data;
};

export async function verifyLoanOTP(otp) {
  const { data } = await axiosInstance.post(`/admin/verifyLoanOTP`, {otp});
  return data;
};

export const getSubscriptionalData = async () => {
  const { data } = await axiosInstance.get("/admin/subscriptions");
  return Array.isArray(data) ? data : data?.data ?? [];
}

export const upsertAccessRole = async (payload) => {
  const { data } = await axiosInstance.post("/admin/access-role", { payload });
  return Array.isArray(data) ? data : data?.data ?? [];
}

export const getAccessRole = async () => {
  const { data } = await axiosInstance.get("/admin/access-role",);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export async function deleteRoleById(roleId) {
  const { data } = await axiosInstance.delete(`/admin/access-role/${roleId}`);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export const getCompanyAdmins = async () => {
  const { data } = await axiosInstance.get("/admin/company-admins");
  return Array.isArray(data) ? data : data?.data ?? [];
}

export const inviteCompanyAdmin = async (payload) => {
  const { data } = await axiosInstance.post("/admin/users/invite", payload);
  return Array.isArray(data) ? data : data?.data ?? [];
};

export const updateCompanyAdmin = async (payload) => {
  // payload: { id, name, roleId, status } -> { user, unassigned_reports }
  const { data } = await axiosInstance.patch(`/admin/users/${payload.id}`, payload);
  return Array.isArray(data) ? data : data?.data ?? [];
};

export const deleteAdminUser = async (userId) => {
  const { data } = await axiosInstance.delete(`/admin/users/${userId}`);
  return Array.isArray(data) ? data : data?.data ?? [];
};

export async function bulkDeleteEmployees(employeeIds) {
  const { data } = await axiosInstance.delete(`/admin/bulk/employees`, {
    data: { employee_ids: employeeIds },
  });
  return data?.data ?? data;
}

export const resendInviteService = async (userId) => {
  const { data } = await axiosInstance.post(`/admin/users/${userId}/resend-invite`);
  return Array.isArray(data) ? data : data?.data ?? [];
}

export const fetchAdminProfile = async () => {
  const { data } = await axiosInstance.get(`/admin/profile`);
  return data;
}

export const updateCompanyAdminProfile = async (payload) => {
  const { data } = await axiosInstance.put(`/admin/profile`, payload);
  return data;
}

export async function uploadAdminProfilePhotoService(formData) {
  const { data } = await axiosInstance.post(`/admin/profilePhoto`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function removeAdminProfilePhotoService() {
  const { data } = await axiosInstance.delete(`/admin/profilePhoto`);
  return data;
}
